import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';

import { setDataAction } from 'redux/actions/shortFormActions';
import { getAge } from '../../components/helpers';
import { creditScoreArray } from '../../utils/constants';
import { Typography, colors, Radio, Dropdown, Input, Button, Checkbox } from '@leapeasy/ui-kit';
import { CustomInputWrapper } from 'components/CustomWrapper';
import { validationMessages } from 'utils/validationMessages';

const Form4 = (props) => {
  const { handleNext, handleBack } = props;
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.shortFormData);

  const [data, setFormData] = useState({
    isStudent: null,
    isCitizen: null,
    consentTexts: null,
    creditScore: null,
    passport: null,
    ssnCheck: false,
  });

  const [valid, setValid] = useState({
    ssn: true,
  });
  const [openAlert, setOpenAlert] = useState(false);

  useEffect(() => {
    setFormData({
      isStudent: formData.data.isStudent,
      isCitizen: formData.data.isCitizen === undefined ? true : formData.data.isCitizen,
      consentTexts: formData.data.consentTexts,
      ssn: formData.data.ssn,
      ssnCheck: formData.data.ssnCheck,
      creditScore: formData.data.creditScore,
      passport: formData.data.passport,
    });
  }, [formData]);

  const handleChange = (e, label) => {
    const value = e.target.value;

    setFormData({
      ...data,
      [label]: value === 'true' ? true : value === 'false' ? false : value,
    });

    if (label === 'ssn') {
      setValid({ ...valid, ssn: value.length === 11 ? true : false });
    } else {
      setValid({ ...data, [label]: true });
    }
  };

  const handleCheckBox = (e, label) => {
    const value = e.target.checked;
    if (label === 'ssnCheck' && value) {
      setOpenAlert('ssn');
    }
    setFormData({
      ...data,
      [label]: value,
    });
  };

  const risk = () => {
    const age = formData.data.age;
    if (data.isCitizen && data.isStudent) {
      return 'Risk Class 1 - College and Post-graduate Students that are U.S. Citizens';
    } else if (!data.isCitizen && data.isStudent) {
      return 'Risk Class 2 - College and Post-graduate Students that are non-U.S. Citizens';
    } else if (data.isCitizen && !data.isStudent && formData.data.studentHousing) {
      return 'Risk Class 3 - First Time Renters that are U.S. citizens';
    } else if (data.isCitizen && !data.isStudent && !formData.data.studentHousing && age < 28) {
      return 'Risk Class 3 - First Time Renters that are U.S. citizens';
    } else if (!data.isCitizen && !data.isStudent) {
      return 'Risk Class 4 - First Time Renters that are non-U.S. citizens';
    } else if (data.isCitizen && !data.isStudent && age >= 28) {
      return 'Risk Class 5 - General Population';
    } else if (!data.isCitizen && !data.isStudent && age > 28) {
      return 'Risk Class 6 - Non-U.S. Employees on assignment in the U.S.';
    }
  };

  const handleNextButton = () => {
    dispatch(
      setDataAction({
        ...formData.data,
        isCitizen: data.isCitizen,
        consentTexts: data.consentTexts,
        ssn: data.ssn,
        ssnCheck: data.ssnCheck,
        riskClass: risk(),
        age: getAge(data.birthDate),
        creditScore: data.creditScore,
        passport: data.passport,
      }),
    );

    handleNext();
  };

  let disable = true;

  if (data.isCitizen) {
    disable = data.ssnCheck && valid.ssn ? false : true;
  } else {
    disable = data.passport ? false : true;
  }

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="20px">
        <Box>
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            U.S. Citizen or Permanent Resident?
          </Typography>

          <Box display="flex" alignItems="flex-start" sx={{ gap: '24px', marginTop: 2 }}>
            <Radio
              label="Yes"
              value={data.isCitizen === true}
              onChange={(_) => handleChange({ target: { value: true } }, 'isCitizen')}
            />

            <Radio
              label="No"
              value={data.isCitizen === false}
              onChange={(_) => handleChange({ target: { value: false } }, 'isCitizen')}
            />
          </Box>
        </Box>

        {typeof data.isCitizen === 'boolean' && (
          <Box
            sx={{
              borderRadius: '8px',
              border: '1px solid #F8F4F9',
              padding: '24px',
              gap: '12px',
              background: 'rgba(249, 248, 249, 0.8)',
              color: colors.purple[900],
            }}
            display="flex"
            flexDirection="column"
          >
            {data.isCitizen ? (
              <>
                {!data.isStudent && (
                  <>
                    <Typography variant="body1">
                      What would you estimate your credit score to be?
                    </Typography>

                    <Box>
                      <Dropdown
                        value={data.creditScore}
                        size="medium"
                        onChange={(e) => handleChange(e, 'creditScore')}
                        options={creditScoreArray}
                        multiselect={false}
                        placeholder="Select item"
                        showSearch={true}
                        style={{
                          minWidth: '100%',
                        }}
                      />
                    </Box>
                  </>
                )}

                <Typography variant="body1">Social Security Number</Typography>
                <Typography variant="small">
                  All sensitive information is encrypted for your security and protection. Click the
                  lock icon to show and hide your SSN.
                </Typography>
                <CustomInputWrapper>
                <Input
                  value={data.ssn}
                  onChange={(e) => handleChange(e, 'ssn')}
                  helperMessage={data.ssn && !valid.ssn ? validationMessages.ssn : ''}
                  type="password-mask"
                  mask="111-11-1111"
                  errorType={data.ssn && !valid.ssn ? 'error' : 'none'}
                  minWidth="100%"
                  size="medium"
                  showCopy={false}
                  fullWidth
                /></CustomInputWrapper>

                <Box display="flex" gap="8px" alignItems="center">
                  <Checkbox
                    value={data.ssnCheck}
                    onChange={(val) => handleCheckBox({ target: { checked: val } }, 'ssnCheck')}
                  />

                  <Typography variant="small">
                    By checking the box, I authorize Leap Insurance Agency to pull my credit*
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body1">Passport Number</Typography>
                <CustomInputWrapper>
                <Input
                  value={data.passport}
                  onChange={(e) => handleChange(e, 'passport')}
                  type="text"
                  minWidth="100%"
                  size="medium"
                  showCopy={false}
                  fullWidth
                /></CustomInputWrapper>
              </>
            )}
          </Box>
        )}

        <Box display="flex" gap="8px">
          <Checkbox
            value={data.consentTexts}
            onChange={(val) => handleCheckBox({ target: { checked: val } }, 'consentTexts')}
          />

          <Typography variant="small">
            By checking this box, I acknowledge that Leap Insurance Agency, LLC. has permission to send text updates about my application process as well as account alerts such as payment reminders. To opt-out, text STOP at any time. For help, text HELP. Message and data rates may apply. Message frequency may vary.
            <Typography variant="small" style={{ color: colors.purple[700] }}>
              <a href="https://www.leapeasy.com/sms-terms-conditions/">* View Mobile Terms*.</a>{' '}
              {'\u00A0'}
              <a href="https://www.leapeasy.com/privacy-policy/">View Privacy Policy.</a>
            </Typography>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', mt: 4, gap: '16px' }}
      >
        <Button size="medium" variant="secondary" onClick={handleBack} style={{ flexShrink: 2 }}>
          Back
        </Button>

        <Button
          size="medium"
          onClick={handleNextButton}
          disabled={disable}
          style={{ flexShrink: 1 }}
        >
          Next
        </Button>
      </Box>

      <Dialog onClose={() => setOpenAlert(null)} open={!!openAlert}>
        <DialogTitle sx={{ padding: '24px 20px' }}>
          <Typography variant="body1">
            Please note that we have minimum credit requirements and your application could be
            declined based on underwriting criteria.
          </Typography>
        </DialogTitle>
        <DialogActions sx={{ padding: '0px 20px 24px 20px' }}>
          <Button size="medium" variant="primary" onClick={() => setOpenAlert(false)}>
            I Understand
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default Form4;
